import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, DollarSign, Info, CheckCircle, AlertCircle } from 'lucide-react';
import ImageUploadV2 from '../../Component/v2/common/ImageUploadV2';

const PublicRegistrationForm = () => {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [event, setEvent] = useState(null);
    const [error, setError] = useState(null);
    const [selectedNormalTicket, setSelectedNormalTicket] = useState(null);
    const [selectedCategoryTicket, setSelectedCategoryTicket] = useState(null);
    const [showTicketInfo, setShowTicketInfo] = useState(null);
    const [formData, setFormData] = useState({});
    const [selectedCategoryId, setSelectedCategoryId] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const participantFormRef = useRef(null);

    useEffect(() => {
        fetchEvent();
    }, [eventId]);

    const fetchEvent = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}`);
            const data = await response.json();
            if (data.success) {
                setEvent(data.data);
                initializeFormData(data.data);
            } else {
                setError('Event not found');
            }
        } catch (err) {
            setError('Failed to load event');
        } finally {
            setLoading(false);
        }
    };

    const initializeFormData = (eventData) => {
        const initialData = {};
        eventData.participantFields?.forEach(field => {
            initialData[field.fieldName] = '';
        });
        setFormData(initialData);
    };

    const normalTickets = event?.tickets?.filter(t => t.ticketType === 'normal' && t.isActive) || [];
    const categoryTickets = event?.tickets?.filter(t => t.ticketType === 'category' && t.isActive) || [];

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatDateRange = (startDate, endDate) => {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start.toDateString() === end.toDateString()) {
            return formatDate(startDate);
        }

        return `${formatDate(startDate)} - ${formatDate(endDate)}`;
    };

    const getTicketPrice = (ticket) => {
        if (ticket.discountPrice && ticket.discountPrice < ticket.price) {
            return (
                <div className="flex items-center gap-2">
                    <span className="text-2xl font-bold text-green-600">₹{ticket.discountPrice}</span>
                    <span className="text-lg text-gray-400 line-through">₹{ticket.price}</span>
                </div>
            );
        }
        return <span className="text-2xl font-bold text-gray-900">₹{ticket.price}</span>;
    };

    const handleTicketSelect = (ticket, type) => {
        if (type === 'normal') {
            const isDeselecting = selectedNormalTicket?.id === ticket.id;
            setSelectedNormalTicket(isDeselecting ? null : ticket);
            if (!isDeselecting) {
                setTimeout(() => {
                    participantFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
            }
        } else {
            const isDeselecting = selectedCategoryTicket?.id === ticket.id;
            setSelectedCategoryTicket(isDeselecting ? null : ticket);
            if (!isDeselecting) {
                setTimeout(() => {
                    participantFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 50);
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (event.eventType === 'paid' && !selectedNormalTicket && !selectedCategoryTicket) {
            alert('Please select at least one ticket');
            return;
        }

        // Check required fields
        const missingFields = event.participantFields
            .filter(field => field.validation === 'required' && !formData[field.fieldName])
            .map(field => field.fieldName);

        if (missingFields.length > 0) {
            alert(`Please fill in required fields: ${missingFields.join(', ')}`);
            return;
        }

        setSubmitting(true);

        try {
            const payload = {
                eventId: event._id,
                data: formData,
                ticketId: selectedNormalTicket?.id || selectedCategoryTicket?.id,
                ...(selectedCategoryId && { categoryId: selectedCategoryId }),
            };

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v2/events/${eventId}/participants`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.success) {
                alert('Registration successful!');
                navigate(`/registration-success/${result.data._id}`);
            } else {
                alert('Registration failed: ' + (result.message || 'Unknown error'));
            }
        } catch (err) {
            alert('Registration failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading event...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center p-8 bg-white rounded-lg shadow-md">
                    <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">Event Not Found</h1>
                    <p className="text-gray-600">{error}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                {/* Event Header */}
                <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8">
                    {event.defaultIdCardBackground && (
                        <div className="h-48 bg-gradient-to-r from-blue-500 to-indigo-600 relative">
                            <img
                                src={event.defaultIdCardBackground}
                                alt="Event"
                                className="w-full h-full object-cover opacity-50"
                            />
                        </div>
                    )}
                    <div className="p-8">
                        <h1 className="text-4xl font-bold text-gray-900 mb-4">{event.eventName}</h1>
                        <div className="flex flex-wrap gap-4 text-gray-600">
                            <div className="flex items-center gap-2">
                                <Calendar className="w-5 h-5" />
                                <span>{formatDateRange(event.eventStartDate, event.eventEndDate)}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users className="w-5 h-5" />
                                <span>{event.eventType === 'free' ? 'Free Event' : 'Paid Event'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Ticket Selection - Only for Paid Events */}
                {event.eventType === 'paid' && (normalTickets.length > 0 || categoryTickets.length > 0) && (
                    <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Your Tickets</h2>

                        {event.bookingType === 'single' && (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <p className="text-sm text-blue-800">
                                    <Info className="w-4 h-4 inline mr-2" />
                                    You can select 1 normal ticket and 1 category ticket
                                </p>
                            </div>
                        )}

                        {/* Normal Tickets */}
                        {normalTickets.length > 0 && (
                            <div className="mb-8">
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Regular Tickets</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {normalTickets.map(ticket => (
                                        <div
                                            key={ticket.id}
                                            onClick={() => handleTicketSelect(ticket, 'normal')}
                                            className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${selectedNormalTicket?.id === ticket.id
                                                    ? 'border-blue-500 bg-blue-50 shadow-lg'
                                                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                                                }`}
                                        >
                                            {selectedNormalTicket?.id === ticket.id && (
                                                <CheckCircle className="absolute top-4 right-4 w-6 h-6 text-blue-600" />
                                            )}
                                            <h4 className="text-lg font-bold text-gray-900 mb-2">{ticket.name}</h4>
                                            {ticket.subText && (
                                                <p className="text-sm text-gray-600 mb-3">{ticket.subText}</p>
                                            )}
                                            {getTicketPrice(ticket)}
                                            {ticket.description && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowTicketInfo(ticket);
                                                    }}
                                                    className="mt-3 text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                                                >
                                                    <Info className="w-4 h-4" />
                                                    More Info
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Category Tickets */}
                        {categoryTickets.length > 0 && (
                            <div>
                                <h3 className="text-xl font-semibold text-gray-800 mb-4">Category Tickets</h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {categoryTickets.map(ticket => (
                                        <div
                                            key={ticket.id}
                                            onClick={() => handleTicketSelect(ticket, 'category')}
                                            className={`relative p-6 border-2 rounded-xl cursor-pointer transition-all ${selectedCategoryTicket?.id === ticket.id
                                                    ? 'border-purple-500 bg-purple-50 shadow-lg'
                                                    : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
                                                }`}
                                        >
                                            {selectedCategoryTicket?.id === ticket.id && (
                                                <CheckCircle className="absolute top-4 right-4 w-6 h-6 text-purple-600" />
                                            )}
                                            <div className="inline-block px-3 py-1 bg-purple-100 text-purple-700 text-xs font-semibold rounded-full mb-2">
                                                CATEGORY
                                            </div>
                                            <h4 className="text-lg font-bold text-gray-900 mb-2">{ticket.name}</h4>
                                            {ticket.subText && (
                                                <p className="text-sm text-gray-600 mb-3">{ticket.subText}</p>
                                            )}
                                            {getTicketPrice(ticket)}
                                            {ticket.description && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setShowTicketInfo(ticket);
                                                    }}
                                                    className="mt-3 text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                                >
                                                    <Info className="w-4 h-4" />
                                                    More Info
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Category Selection — free events only, only visible categories */}
                {event.eventType === 'free' && (() => {
                    const visibleCats = (event.participantCategories || []).filter(c => c.showInForm !== false);
                    if (visibleCats.length === 0) return null;
                    return (
                        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Your Category</h2>
                            <p className="text-sm text-gray-500 mb-6">Choose the category that best describes your role at this event.</p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {visibleCats.map(cat => {
                                    const isSelected = selectedCategoryId === cat.id;
                                    return (
                                        <button
                                            key={cat.id}
                                            type="button"
                                            onClick={() => setSelectedCategoryId(isSelected ? '' : cat.id)}
                                            className={`relative text-left p-5 rounded-xl border-2 transition-all ${
                                                isSelected
                                                    ? 'border-blue-500 bg-blue-50 shadow-md'
                                                    : 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
                                            }`}
                                        >
                                            {isSelected && (
                                                <CheckCircle className="absolute top-3 right-3 w-5 h-5 text-blue-600" />
                                            )}
                                            {cat.idCardBackground && (
                                                <div className="w-10 h-14 rounded-md overflow-hidden border border-gray-200 mb-3 float-right ml-3">
                                                    <img src={cat.idCardBackground} alt={cat.name} className="w-full h-full object-cover" />
                                                </div>
                                            )}
                                            <p className="font-semibold text-gray-900 text-base">{cat.name}</p>
                                            {cat.description && (
                                                <p className="text-sm text-gray-500 mt-1">{cat.description}</p>
                                            )}
                                            {cat.amenities?.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {cat.amenities.map(a => (
                                                        <span key={a} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full border border-emerald-100">
                                                            {a}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })()}

                {/* Registration Form */}
                <form ref={participantFormRef} onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Participant Information</h2>

                    <div className="space-y-6">
                        {event.participantFields?.map(field => (
                            <div key={field.id}>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {field.fieldName}
                                    {field.validation === 'required' && (
                                        <span className="text-red-500 ml-1">*</span>
                                    )}
                                </label>

                                {field.fieldType === 'file' ? (
                                    <ImageUploadV2
                                        onUploadSuccess={(url) => setFormData({ ...formData, [field.fieldName]: url })}
                                        onUploadError={(error) => console.error('Upload error:', error)}
                                        uploadEndpoint="/api/v2/events/upload/participant-profile"
                                        currentImageUrl={formData[field.fieldName]}
                                        label=""
                                    />
                                ) : field.fieldType === 'textarea' ? (
                                    <textarea
                                        value={formData[field.fieldName] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
                                        placeholder={field.placeholder}
                                        required={field.validation === 'required'}
                                        rows={4}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                ) : field.fieldType === 'select' ? (
                                    <select
                                        value={formData[field.fieldName] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
                                        required={field.validation === 'required'}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    >
                                        <option value="">Select {field.fieldName}</option>
                                        {field.options?.map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                ) : (
                                    <input
                                        type={field.fieldType}
                                        value={formData[field.fieldName] || ''}
                                        onChange={(e) => setFormData({ ...formData, [field.fieldName]: e.target.value })}
                                        placeholder={field.placeholder}
                                        required={field.validation === 'required'}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                )}

                                {field.helpText && (
                                    <p className="mt-1 text-sm text-gray-500">{field.helpText}</p>
                                )}
                            </div>
                        ))}
                    </div>

                    <button
                        type="submit"
                        disabled={submitting}
                        className="mt-8 w-full bg-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                        {submitting ? 'Submitting...' : 'Complete Registration'}
                    </button>
                </form>

                {/* Ticket Info Modal */}
                {showTicketInfo && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                        <div className="bg-white rounded-2xl p-8 max-w-2xl w-full max-h-[80vh] overflow-y-auto">
                            <h3 className="text-2xl font-bold text-gray-900 mb-4">{showTicketInfo.name}</h3>
                            <p className="text-gray-700 mb-6 whitespace-pre-wrap">{showTicketInfo.description}</p>

                            {showTicketInfo.priceByDate && showTicketInfo.priceByDate.length > 0 && (
                                <div className="mb-6">
                                    <h4 className="font-semibold text-gray-900 mb-3">Date-wise Pricing</h4>
                                    <div className="space-y-2">
                                        {showTicketInfo.priceByDate.map((pd, idx) => (
                                            <div key={idx} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                                                <span className="text-sm text-gray-700">
                                                    {new Date(pd.startDate).toLocaleDateString()} - {new Date(pd.endDate).toLocaleDateString()}
                                                </span>
                                                <span className="font-semibold text-gray-900">₹{pd.price}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={() => setShowTicketInfo(null)}
                                className="w-full bg-gray-200 text-gray-800 py-3 px-6 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PublicRegistrationForm;
